<script setup lang="ts">
import { enabled } from 'virtual:nuxt-pwa-configuration'
import 'moment/dist/locale/de'

const { siteName, appVersion } = useAppConfig()
useValidation()
useHead({
  titleTemplate: (titleChunk?: string): string => {
    return titleChunk ? `${titleChunk} - ${siteName}` : String(siteName)
  },
  meta: [
    { name: 'version', content: `${appVersion}` },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
    }
  ]
})

const { $pwa } = useNuxtApp()
const { t } = useL10n()
toast.value = useToast()
const { logout, isLoggedIn } = useUserStore()
const languageStore = useLanguageStore()

const applyDefaultColor = () => {
  useCssVar('--primary-color-50').value = '#F3F6FD'
  useCssVar('--primary-color-100').value = '#E8EEFA'
  useCssVar('--primary-color-200').value = '#C7D6F2'
  useCssVar('--primary-color-300').value = '#9EB7E8'
  useCssVar('--primary-color-400').value = '#6F92D8'
  useCssVar('--primary-color-500').value = '#1C4996'
  useCssVar('--primary-color-600').value = '#163D7D'
  useCssVar('--primary-color-700').value = '#113064'
  useCssVar('--primary-color-800').value = '#0C234A'
  useCssVar('--primary-color-900').value = '#08172F'
  useCssVar('--primary-color-950').value = '#040C1A'
  useCssVar('--p-primary-50').value = '#F3F6FD'
  useCssVar('--p-primary-100').value = '#E8EEFA'
  useCssVar('--p-primary-200').value = '#C7D6F2'
  useCssVar('--p-primary-300').value = '#9EB7E8'
  useCssVar('--p-primary-400').value = '#6F92D8'
  useCssVar('--p-primary-500').value = '#1C4996'
  useCssVar('--p-primary-600').value = '#163D7D'
  useCssVar('--p-primary-700').value = '#113064'
  useCssVar('--p-primary-800').value = '#0C234A'
  useCssVar('--p-primary-900').value = '#08172F'
  useCssVar('--p-primary-950').value = '#040C1A'
  useCssVar('--p-primary-color').value = '#1C4996'
  useCssVar('--primary-color').value = '#1C4996'
  useCssVar('--surface-500').value = '#f5f5f5'
}

onMounted(async () => {
  applyDefaultColor()
  provideGlobalState()

  // Force service worker registration and wait for it to be ready
  if (enabled && 'serviceWorker' in navigator) {
    try {
      await navigator.serviceWorker.ready
    } catch (error) {
      toast.value?.add({ severity: 'warn', detail: 'Offline features may not work' })
    }
  }

  if (!isLoggedIn) {
    navigateTo('/login')
  }

  document.addEventListener('mousedown', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-select')) {
      event.preventDefault()
    }
  })

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-select')) {
      event.preventDefault()
    }
  })

  document.addEventListener('mousedown', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-multiselect')) {
      event.preventDefault()
    }
  })

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    if (target.closest('.p-multiselect')) {
      event.preventDefault()
    }
  })

  menuItems.value = [
    {
      label: t('global.userMenu.reload'),
      icon: 'pi pi-refresh',
      command: () => {
        $pwa?.updateServiceWorker(true)
      }
    },
    {
      label: t('global.userMenu.logout'),
      icon: 'pi pi-sign-out',
      command: () => {
        logout()
      }
    }
  ]
})
</script>
<template>
  <div>
    <NuxtLayout :key="languageStore.language">
      <VitePwaManifest />
      <NuxtPage />
      <Toast />
    </NuxtLayout>
  </div>
</template>
<style lang="scss">
html {
  // Keep the scrollbar gutter reserved while a modal blocks body scroll —
  // otherwise the vanishing scrollbar shifts the whole layout on open/close.
  scrollbar-gutter: stable;
}
body {
  background-color: var(--surface-500);
  padding: 20px;
  max-height: 100vh;
}
div {
  line-height: normal;
}
.p-card {
  background-color: white !important;
  border-radius: 0.25rem !important;
  padding: 0.75rem !important;
}
.p-card-body {
  padding: 4px !important;
}
.p-card-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 0;
  padding-bottom: 0;
}
.p-component {
  font-size: 0.875rem;
}
.p-inputtext::placeholder,
.p-textarea::placeholder,
.p-placeholder {
  color: var(--p-surface-400) !important;
  font-size: 14px;
}
.p-multiselect .p-multiselect-label.p-placeholder {
  color: var(--p-surface-400) !important;
  font-size: 14px;
}
.error {
  color: crimson;
  font-weight: 500;
}
.p-select {
  z-index: 1 !important;
}
.p-select-option-label {
  white-space: pre-line;
}
.p-toast {
  position: fixed;
  top: 0;
  right: 0;
  left: 20px;
  z-index: 1000;
  width: 80% !important;
}
.image-editor {
  height: 24rem;
}
.fieldTitle {
  color: var(--primary-color);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.buttonHeader {
  background-color: var(--primary-500) !important;
  border: 0 !important;
  padding: 0.5rem 1.8rem !important;
}
.p-button:not(.p-button-secondary):not(.p-button-text):not(.p-button-outlined) {
  .p-button-label {
    color: white !important;
  }
}
.p-menu-item-label,
.p-menu-item-icon {
  color: var(--text-color) !important;
}
.p-iftalabel {
  input {
    border-radius: 14px;
    padding: 1rem;
  }
  .p-select ~ label {
    z-index: 1;
  }
  .p-select-label {
    padding-inline-start: 1rem;
  }
  .p-textarea {
    padding-left: 1rem;
  }
  label {
    margin-top: 0.3rem;
    margin-left: 0.4rem;
  }
}
.groupTitle {
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: black;
}
.selectInput {
  width: 100%;
  height: 3.5rem;
  align-items: center;

  .p-datepicker-input,
  .p-inputnumber-input,
  button {
    height: 100%;
  }
}
.checkboxGroup {
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.submitButton {
  width: 100% !important;
  padding: 1.25rem !important;
  border-radius: 30px !important;
  margin-top: 1.25rem !important;
}
.signaturePad,
canvas {
  border-style: solid;
  border-width: 1px;
  border-color: #d1d5db;
  background-color: white;
  border-radius: 14px;
  width: 100% !important;
}
.p-multiselect {
  background: #ffffff;
  height: min-content;

  .p-multiselect-label {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    color: #495057 !important;
  }
}

.p-multiselect-filter {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  font-size: 0.875rem;
}

.p-multiselect-dropdown,
.p-select-dropdown {
  width: unset;
  min-width: 3rem;
  padding-right: 0.5rem;
  pointer-events: auto;
}
.changeInputTypeButton {
  border: none !important;
  background-color: transparent !important;
  padding: 0 !important;

  .p-button-label {
    color: var(--primary-color) !important;
  }
  &:hover {
    background-color: transparent !important;
    border: none !important;
  }
}
.todaysAppointments,
.todaysReports {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1rem;
  color: #4b5563 !important;
}
.tabs-container {
  width: 100%;
  overflow: hidden;
}
.tabs-container .p-tab {
  width: 33.333%;
  height: 68px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  border-width: 3px 0 0 0;

  span {
    color: #64748b;
    font-weight: 600;
  }

  i {
    font-size: 1.5rem;
  }
}
.p-tab-active span {
  color: var(--primary-color) !important;
}
.p-select {
  outline: none;
}
.p-select:focus {
  outline: none;
}
.p-multiselect {
  outline: none;
}
.p-multiselect:focus {
  outline: none;
}
.p-textarea,
.p-inputtext {
  font-size: 14px !important;
}
.p-dialog-header,
.dialog-header {
  background: var(--primary-600, #1c4996) !important;
  border-bottom: none;
  border-radius: 12px 12px 0 0 !important;

  .p-button {
    color: white !important;

    &:hover {
      background: rgba(255, 255, 255, 0.15) !important;
    }
  }
}

.p-dialog-header-actions .p-button {
  color: white !important;

  &:hover {
    background: rgba(255, 255, 255, 0.15) !important;
  }
}

.p-dialog-title {
  color: white;
}

.p-dialog-content {
  padding-top: 1rem !important;
}
</style>
